import { Device, Geotab, Group, GroupFilterCondition, Trip, User } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { uniqBy } from "lodash";
import sortBy from "lodash/sortBy";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import createGroupFilter from "@/utilities/createGroupFilter";
import {
  fetchTripsComplete,
  GetTrips,
  TripSearchTerm,
} from "@/utilities/query/fetchTripsComplete";

interface Args {
  users?: User[];
  devices?: Device[];
  toDate: Date;
  fromDate: Date;
}

/** How many vehicles a group filter may resolve to. */
const DEVICES_PER_GROUP_QUERY = 50_000;

/**
 * Every trip in a date range, for the given drivers or vehicles.
 *
 * Complete: each search is paged until the window is covered and the combined result is not
 * capped. It used to stop at a thousand trips per search and a thousand overall — fine for a
 * screen someone scrolls, wrong for a logbook that has to be handed on, because a wider period
 * simply lost rows with nothing to say so.
 */
export function useTripsByDateRange({ users, devices, toDate, fromDate }: Args) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();

  return useQuery({
    // The group filter decides which devices are searched, so it belongs in the key — otherwise
    // switching groups served the trips of the previously selected group. Only the group ids go
    // in: React Query re-hashes the key by value on every render, and full group records carry
    // far more than identity.
    queryKey: [
      "trips",
      activeSessionId,
      {
        users,
        devices,
        toDate,
        fromDate,
        groupIds: groups?.map(g => g.id),
        groupFilterCondition,
      },
    ],

    queryFn: async ({ signal }) => {
      const searches = await searchTerms(
        geotabClient,
        { users, devices, groups, groupFilterCondition },
        signal,
      );

      const trips = await fetchTripsComplete(getTrips(geotabClient, signal), {
        fromDate,
        toDate,
        searches,
        signal,
      });

      // Deduplicated because two searches can name the same trip — a driver and the vehicle they
      // drove — and sorted because the pages arrive per search, not in time order.
      return uniqBy(
        sortBy(trips, t => t.start),
        t => t.id,
      );
    },
  });
}

function getTrips(geotabClient: Geotab, signal?: AbortSignal): GetTrips {
  return (search, resultsLimit) =>
    geotabClient.get("Trip", search, resultsLimit, signal) as Promise<Trip[]>;
}

interface Selection {
  users?: User[];
  devices?: Device[];
  groups?: Group[];
  groupFilterCondition?: GroupFilterCondition;
}

/**
 * What to ask Geotab about: one search per driver, per vehicle, or per vehicle of the group.
 *
 * An empty search means "everything the session may see", which is what an unfiltered page with
 * no group selection wants.
 */
async function searchTerms(
  geotabClient: Geotab,
  { users, devices, groups, groupFilterCondition }: Selection,
  signal?: AbortSignal,
): Promise<TripSearchTerm[]> {
  if (users && users.length > 0) {
    return users.map(u => ({ userSearch: { id: u.id } }));
  }

  if (devices && devices.length > 0) {
    return devices.map(d => ({ deviceSearch: { id: d.id } }));
  }

  if (!groups && !groupFilterCondition) {
    return [{}];
  }

  const filter = createGroupFilter(groups, groupFilterCondition, {});
  const groupDevices = await geotabClient.get(
    "Device",
    filter,
    DEVICES_PER_GROUP_QUERY,
    signal,
  );

  return groupDevices.map(d => ({ deviceSearch: { id: d.id } }));
}
